import { CubicBezier, } from "./bezier.js";
import { TermSummationExpression } from "../../math/expressions/expressions.js";
import { Independent } from "../../math/expressions/independent.js";
import { Term } from "../../math/expressions/term.js";
import { factorial } from "../../math/factorial.js";
import { Polynomial } from "../../math/polynomial.js";
import { equalsTester, setIntersection } from "../../util/math.js";
import { toSuper } from "../../util/string-util.js";
/*
Silesian J. Pure Appl. Math.
vol. 6, is. 1 (2016), 155–176

Barbara BILY
Institute of Mathematics, Silesian University of Technology, Gliwice, Poland

THE METHOD OF FINDING POINTS OF INTERSECTION OF TWO CUBIC BÉZIER
CURVES USING THE SYLVESTER MATRIX

Abstract. Sylvester matrix is used to create a 9th degree polynomial
from coefficients of two cubic Bézier curves. The real roots of this polynomial
allow to compute points of intersection of aforementioned curves.
An additional constraint is used to indicate valid points. The “reverse-
inverse law” is presented in order to reduce the cost of calculation in this
particular case. Also some limitations of the method as well as the ways to
avoid them, if possible, are pointed out.

https://mat.polsl.pl/sjpam/zeszyty/z6/Silesian_J_Pure_Appl_Math_v6_i1_str_155-176.pdf
 */
export class CubicCubicIntersections {
    /**
     * Calculate all intersections between curve a and curve b based on the method
     * described in https://mat.polsl.pl/sjpam/zeszyty/z6/Silesian_J_Pure_Appl_Math_v6_i1_str_155-176.pdf
     * @param a Cubic Bezier curve a
     * @param b Cubic Bezier curve b
     * @param rounder An optional rounder to allow some imprecision
     * @returns list of intersection found in ascending order of t for curve a
     */
    static findIntersections(a, b, rounder, research = false, debug = false) {
        const intersections = [];
        const t = CubicCubicIntersections.findIntersectionsT(a, b, rounder, research, debug);
        if (t.length > 0) {
            const _rounder = rounder ? rounder : (v) => v;
            const coordinateRounder = (c) => ({
                x: _rounder(c.x),
                y: _rounder(c.y),
            });
            t.forEach((t) => {
                const coordinate = coordinateRounder(CubicBezier.get(a, t));
                intersections.push(coordinate);
                if (debug === true) {
                    console.log(`intersection for ta=${t} at [${coordinate.x}, ${coordinate.y}]`);
                }
            });
        }
        return intersections;
    }
    /**
     * Calculate all t for curve a where curve a intersects curve b based on the method
     * described in https://mat.polsl.pl/sjpam/zeszyty/z6/Silesian_J_Pure_Appl_Math_v6_i1_str_155-176.pdf
     * @param a Cubic Bezier curve a
     * @param b Cubic Bezier curve b
     * @param rounder An optional rounder to allow some imprecision
     * @returns list of t for curve a found in ascending order
     */
    static findIntersectionsT(a, b, rounder, research = false, debug = false) {
        const scalers = CubicCubicIntersections.getScalers(a, b);
        if (scalers) {
            //bounding boxes do overlap so down into the rabbit hole
            const { xScale, yScale } = scalers;
            const _rounder = rounder ? rounder : (v) => v;
            const coordinateRounder = (c) => ({
                x: _rounder(c.x),
                y: _rounder(c.y),
            });
            const eqCoordinate = (c0, c1) => c0.x == c1.x && c0.y == c1.y;
            //figure out the special cases
            const sa = coordinateRounder(CubicBezier.getStart(a));
            const ea = coordinateRounder(CubicBezier.getEnd(a));
            const sb = coordinateRounder(CubicBezier.getStart(b));
            const eb = coordinateRounder(CubicBezier.getEnd(b));
            const saEQsb = eqCoordinate(sa, sb);
            const saEQeb = eqCoordinate(sa, eb);
            const eaEQsb = eqCoordinate(ea, sb);
            const eaEQeb = eqCoordinate(ea, eb);
            const solverParameters = {
                saIsRoot: saEQsb || saEQeb, //start curve a equals one end of curve b
                eaIsRoot: eaEQsb || eaEQeb, //end curve a equals one end of curve b
                reverse: sa.x == sb.x //reverse the order of the control points of curve a when both curves have the same start x coordinate
            };
            const _a = CubicBezier.clone(a);
            const _b = CubicBezier.clone(b);
            //scale the curves for numerical stability
            _a.cp.forEach((cp) => {
                cp.x = xScale(cp.x);
                cp.y = yScale(cp.y);
            });
            _b.cp.forEach((cp) => {
                cp.x = xScale(cp.x);
                cp.y = yScale(cp.y);
            });
            //check if the curves overlap and return 10 t's for curve a when they do
            const tOverlap = CubicCubicIntersections.getOverlap(_a, _b);
            if (tOverlap !== undefined)
                return tOverlap;
            if (solverParameters.reverse)
                CubicBezier.reverse(_a);
            //start finding the intersections
            return research ? CubicCubicIntersections.solveInResearchMode(_a, _b, solverParameters, debug) : CubicCubicIntersections.solve(_a, _b, solverParameters, debug);
        }
        else {
            //bounding boxes do not overlap
            if (debug) {
                console.log("no overlap");
            }
            return [];
        }
    }
    static solveInResearchMode(_a, _b, solverParameters, debug) {
        //start finding the intersections
        const vars = CubicCubicIntersections.createVariableMap(_a, _b);
        // some logging to see intermediate results START
        if (debug === true) {
            D3.logExpressions(vars);
        }
        // some logging to see intermediate results END
        const rootsForT = [];
        //deal with the corner cases detected at the start
        // prepend 0 to the list of rootsForT when start
        // of curve a equals one of the end points of curve b
        if (solverParameters.saIsRoot) {
            rootsForT.push(solverParameters.reverse ? 1 : 0);
        }
        // append 1 to the list of rootsForT when end
        // of curve a equals one of the end points of curve b
        if (solverParameters.eaIsRoot) {
            rootsForT.push(solverParameters.reverse ? 0 : 1);
        }
        // solve the determinant equation for det(k)=0 using all coefficients of the
        // original curves a & b
        const rootsForK = CubicCubicIntersections.solveForIndependent(D3.sylMDet, vars, "k");
        // some logging to see intermediate results START
        if (debug === true) {
            console.log("roots for k=", rootsForK);
        }
        // some logging to see intermediate results END
        //create an array with 0 <= k <= 1
        const kSmall = rootsForK.filter((k) => k >= 0.0 && k <= 1.0);
        //create an array with 1 < k
        const kLarge = rootsForK.filter((k) => k > 1.0);
        // plug each kSmall together with all all coefficients of the
        // original curves a & b into the equation bk-a in order to get ta.
        // When 0 <= ta <= 1 we store it in rootsForT. The values in
        // this set can be plugged in to the equation for curve a
        // in order to get the intersections.
        for (let i = 0; i < kSmall.length; i++) {
            const k = kSmall[i];
            CubicCubicIntersections.addVariableToMap("k", k, vars);
            const ta = CubicCubicIntersections.solveIndependent(D3.bkXMinusAX, D3.bkYMinusAY, vars, "t");
            if (debug === true) {
                console.log(`k=${k} : ta=${ta}`);
            }
            if (ta != undefined &&
                ta >= 0.0 &&
                ta <= 1.0 &&
                rootsForT.findIndex(equalsTester(ta, Polynomial.DEFAULT_ERROR)) == -1) {
                rootsForT.push(ta);
            }
        }
        // plug each kLarge together with all all coefficients of the
        // original curves a & b into the equation ak-b in order to get tb
        // When 0 <=tb <= 1 we find ta as above
        for (let i = 0; i < kLarge.length; i++) {
            const k = kLarge[i];
            //due to the reverse-inverse law we can do this.
            CubicCubicIntersections.addVariableToMap("k", 1.0 / k, vars);
            const tb = CubicCubicIntersections.solveIndependent(D3.akXMinusBX, D3.akYMinusBY, vars, "t");
            if (debug === true) {
                console.log(`k=${k} : tb=${tb}`);
            }
            if (tb != undefined && tb >= 0.0 && tb <= 1.0) {
                //find the t for the other curve
                CubicCubicIntersections.addVariableToMap("k", k, vars);
                const ta = CubicCubicIntersections.solveIndependent(D3.bkXMinusAX, D3.bkYMinusAY, vars, "t");
                if (debug === true) {
                    console.log(`k=${k} : ta=${ta}`);
                }
                if (ta != undefined &&
                    ta >= 0.0 &&
                    ta <= 1.0 &&
                    rootsForT.findIndex(equalsTester(ta, Polynomial.DEFAULT_ERROR)) == -1) {
                    rootsForT.push(ta);
                }
            }
        }
        const t = rootsForT
            .map((t) => (solverParameters.reverse ? 1.0 - t : t))
            .sort((a, b) => a - b);
        // some logging to see intermediate results START
        if (debug === true) {
            console.log("roots for t=", t);
        }
        // some logging to see intermediate results END
        // bob is your uncle
        return t;
    }
    static solve(_a, _b, solverParameters, debug) {
        //start finding the intersections
        const vars = CubicCubicIntersections.createVariableMap(_a, _b, 3);
        // some logging to see intermediate results START
        if (debug === true) {
            D3.logExpressions(vars);
        }
        // some logging to see intermediate results END
        const rootsForT = [];
        //deal with the corner cases detected at the start
        // prepend 0 to the list of rootsForT when start
        // of curve a equals one of the end points of curve b
        if (solverParameters.saIsRoot) {
            rootsForT.push(solverParameters.reverse ? 1 : 0);
        }
        // append 1 to the list of rootsForT when end
        // of curve a equals one of the end points of curve b
        if (solverParameters.eaIsRoot) {
            rootsForT.push(solverParameters.reverse ? 0 : 1);
        }
        // solve the determinant equation for det(k)=0 using all coefficients of the
        // original curves a & b
        const Ck = D3.getKCoefficients(vars);
        const rootsForK = Polynomial.rootsOpen(Ck);
        // some logging to see intermediate results START
        if (debug === true) {
            console.log("roots for k=", rootsForK);
        }
        // some logging to see intermediate results END
        //create an array with 0 <= k <= 1
        const kSmall = rootsForK.filter((k) => k >= 0.0 && k <= 1.0);
        //create an array with 1 < k
        const kLarge = rootsForK.filter((k) => k > 1.0);
        // plug each kSmall together with all all coefficients of the
        // original curves a & b into the equation bk-a in order to get ta.
        // When 0 <= ta <= 1 we store it in rootsForT. The values in
        // this set can be plugged in to the equation for curve a
        // in order to get the intersections.
        for (let i = 0; i < kSmall.length; i++) {
            const k = kSmall[i];
            CubicCubicIntersections.addVariableToMap("k", k, vars, 3);
            const Ctax = D3.getTCoefficientsBKXMinusAX(vars);
            const Ctay = D3.getTCoefficientsBKYMinusAY(vars);
            const rootsAX = Polynomial.rootsOpen(Ctax);
            const rootsAY = Polynomial.rootsOpen(Ctay);
            const ta = setIntersection(rootsAX, rootsAY, Polynomial.DEFAULT_ERROR)[0];
            if (debug === true) {
                console.log(`k=${k} : ta=${ta}`);
            }
            if (ta != undefined &&
                ta >= 0.0 &&
                ta <= 1.0 &&
                rootsForT.findIndex(equalsTester(ta, Polynomial.DEFAULT_ERROR)) == -1) {
                rootsForT.push(ta);
            }
        }
        // plug each kLarge together with all all coefficients of the
        // original curves a & b into the equation ak-b in order to get tb
        // When 0 <=tb <= 1 we find ta as above
        for (let i = 0; i < kLarge.length; i++) {
            const k = kLarge[i];
            //due to the reverse-inverse law we can do this.
            CubicCubicIntersections.addVariableToMap("k", 1.0 / k, vars, 3);
            const Ctbx = D3.getTCoefficientsAKXMinusBX(vars);
            const Ctby = D3.getTCoefficientsAKYMinusBY(vars);
            const rootsBX = Polynomial.rootsOpen(Ctbx);
            const rootsBY = Polynomial.rootsOpen(Ctby);
            const tb = setIntersection(rootsBX, rootsBY, Polynomial.DEFAULT_ERROR)[0];
            if (debug === true) {
                console.log(`k=${k} : tb=${tb}`);
            }
            if (tb != undefined && tb >= 0.0 && tb <= 1.0) {
                //find the t for the other curve
                CubicCubicIntersections.addVariableToMap("k", k, vars, 3);
                const Ctax = D3.getTCoefficientsBKXMinusAX(vars);
                const Ctay = D3.getTCoefficientsBKYMinusAY(vars);
                const rootsAX = Polynomial.rootsOpen(Ctax);
                const rootsAY = Polynomial.rootsOpen(Ctay);
                const ta = setIntersection(rootsAX, rootsAY, Polynomial.DEFAULT_ERROR)[0];
                if (debug === true) {
                    console.log(`k=${k} : ta=${ta}`);
                }
                if (ta != undefined &&
                    ta >= 0.0 &&
                    ta <= 1.0 &&
                    rootsForT.findIndex(equalsTester(ta, Polynomial.DEFAULT_ERROR)) == -1) {
                    rootsForT.push(ta);
                }
            }
        }
        const t = rootsForT
            .map((t) => (solverParameters.reverse ? 1.0 - t : t))
            .sort((a, b) => a - b);
        // some logging to see intermediate results START
        if (debug === true) {
            console.log("roots for t=", t);
        }
        // some logging to see intermediate results END
        // bob is your uncle
        return t;
    }
    static getOverlap(a, b) {
        const tBsOnA = CubicBezier.getTForPoint(a, CubicBezier.getStart(b), Polynomial.DEFAULT_ERROR);
        const tBeOnA = CubicBezier.getTForPoint(a, CubicBezier.getEnd(b), Polynomial.DEFAULT_ERROR);
        const tAsOnB = CubicBezier.getTForPoint(b, CubicBezier.getStart(a), Polynomial.DEFAULT_ERROR);
        const tAeOnB = CubicBezier.getTForPoint(b, CubicBezier.getEnd(a), Polynomial.DEFAULT_ERROR);
        let overlapScore = (tBsOnA != undefined ? 8 : 0) +
            (tBeOnA != undefined ? 4 : 0) +
            (tAsOnB != undefined ? 2 : 0) +
            (tAeOnB != undefined ? 1 : 0);
        let tA = undefined;
        let tB = undefined;
        switch (overlapScore) {
            case 3:
            case 7:
            case 11:
                // curve a might be fully on b
                tA = [0.0, 1.0];
                tB = [tAsOnB, tAeOnB];
                //reverse B if tAsOnB > tAeOnB
                //clip B at tAsOnB && tAeOnB  and keep section in the middle
                // a and clipped b should be the same
                break;
            case 12:
            case 13:
            case 14:
            case 15:
                // curve b might be fully on a
                tA = [tBsOnA, tBeOnA];
                tB = [0.0, 1.0];
                //reverse a if tBsOnA > tBeOnA
                //clip A at tBsOnA && tBeOnA and keep section in the middle
                // clipped a and b should be the same
                break;
            case 5:
                //possible partial overlap A 
                tA = [tBeOnA, 1.0];
                tB = [1.0, tAeOnB];
                //clip A at tBeOnA and keep last
                //clip B at tAeOnB and keep last and reverse
                //clips should be the same
                break;
            case 6:
                //possible partial overlap B
                tA = [tBeOnA, 0.0];
                tB = [1.0, tAsOnB];
                //clip A at tBeOnA and keep first and reverse
                //clip B at tAsOnB and keep last and reverse
                //clips should be the same
                break;
            case 9:
                //possible partial overlap C
                tA = [tBsOnA, 1.0];
                tB = [0.0, tAeOnB];
                //clip A at tBsOnA and keep last
                //clip B at tAeOnB and keep first
                //clips should be the same
                break;
            case 10:
                //possible partial overlap D
                tA = [tBsOnA, 0.0];
                tB = [0.0, tAsOnB];
                //clip A at tBsOnA and keep first and reverse
                //clip B at tAsOnB and keep first
                //clips should be the same
                break;
            default:
            //no overlap possible in cases 0, 1, 2, 4 & 8  
        }
        //if full overlap, construct a list t's for a with 10 values for t
        //that lie in the overlapping section with the first and last t
        //defining the overlap boundaries. Then return that list.
        //since 9 is the maximum of proper intersections, a list
        //of 10 roots will designate overlap.
        return undefined;
    }
    /**
     * Constructs a mapping from independents used in the various expressions to their actual
     * values base on the given 2 cubic BezierCurves.
     * @param a Cubic Bezier curve a
     * @param b Cubic Bezier curve b
     * @returns The variable map from independent name to it's value
     */
    static createVariableMap(a, b, maxPower = 1) {
        // polynomial coefficients curve a
        const ax = CubicCubicIntersections.getPolynomialCoefficients(a.cp.map((p) => p.x));
        const ay = CubicCubicIntersections.getPolynomialCoefficients(a.cp.map((p) => p.y));
        // polynomial coefficients curve b
        const bx = CubicCubicIntersections.getPolynomialCoefficients(b.cp.map((p) => p.x));
        const by = CubicCubicIntersections.getPolynomialCoefficients(b.cp.map((p) => p.y));
        const vars = {};
        ax.forEach((c, i) => (vars[`a${i}x`] = c));
        ay.forEach((c, i) => (vars[`a${i}y`] = c));
        bx.forEach((c, i) => (vars[`b${i}x`] = c));
        by.forEach((c, i) => (vars[`b${i}y`] = c));
        //add all these to the power of 2 until maxPower
        const varKeys = Object.keys(vars);
        for (let p = 2; p <= maxPower; p++) {
            varKeys.forEach((key) => {
                vars[key + "_p" + p] = Math.pow(vars[key], p);
            });
        }
        return vars;
    }
    static addVariableToMap(key, value, vars, maxPower = 1) {
        vars[key] = value;
        //add all these to the power of 2 until maxPower
        for (let p = 2; p <= maxPower; p++) {
            vars[key + "_p" + p] = Math.pow(vars[key], p);
        }
        return vars;
    }
    /**
     * Calculates the polynomial coefficients for given coordinate control points.
     * @param p coordinate of the control points
     * @returns the polynomial coefficients in the order of increasing degrees
     */
    static getPolynomialCoefficients(p) {
        const n = p.length - 1; //the degree of the polynomial
        if (n < 1) {
            throw new Error("p.length < 2");
        }
        switch (p.length) {
            case 2:
                return CubicCubicIntersections.getLinearPolynomialCoefficients(p);
            case 3:
                return CubicCubicIntersections.getQuadraticPolynomialCoefficients(p);
            case 4:
                return CubicCubicIntersections.getCubicPolynomialCoefficients(p);
            default:
                //See https://en.wikipedia.org/wiki/B%C3%A9zier_curve#Polynomial_form
                const n_f = factorial(n);
                const c = [];
                for (let j = 0; j <= n; j++) {
                    c.push(0);
                    const numerator = n_f / factorial(n - j);
                    for (let i = 0; i <= j; i++) {
                        const sign = (i + j) % 2 == 0 ? 1 : -1;
                        const denominator = factorial(i) * factorial(j - i);
                        c[j] += p[i] * ((sign * numerator) / denominator);
                    }
                }
                return c;
        }
    }
    /**
     * Calculates the degree 1 polynomial coefficients for given coordinate control points.
     * @param p coordinate of the control points
     * @returns the polynomial coefficients in the order of increasing degrees
     */
    static getLinearPolynomialCoefficients(p) {
        return [1.0 * p[0], -1.0 * p[0] + 1.0 * p[1]];
    }
    /**
     * Calculates the degree 2 polynomial coefficients for given coordinate control points.
     * @param p coordinate of the control points
     * @returns the polynomial coefficients in the order of increasing degrees
     */
    static getQuadraticPolynomialCoefficients(p) {
        return [
            1.0 * p[0],
            -2.0 * p[0] + 2.0 * p[1],
            1.0 * p[0] - 2.0 * p[1] + 1.0 * p[2],
        ];
    }
    /**
     * Calculates the degree 3 polynomial coefficients for given coordinate control points.
     * @param p coordinate of the control points
     * @returns the polynomial coefficients in the order of increasing degrees
     */
    static getCubicPolynomialCoefficients(p) {
        return [
            1.0 * p[0],
            -3.0 * p[0] + 3.0 * p[1],
            3.0 * p[0] - 6.0 * p[1] + 3.0 * p[2],
            -1.0 * p[0] + 3.0 * p[1] - 3.0 * p[2] + 1.0 * p[3],
        ];
    }
    /**
     * Creates a polynomial expression of given degree of the form:
     * a0x + a1x∙t + a2x∙t^2 + .... anx∙t^n
     * where:
     * a = prefix,
     * x = postfix,
     * t = independent,
     * n = degree
     * @param degree  the degree of the polynomial
     * @param prefix the prefix of the coefficients
     * @param postfix the postfix of the coefficients
     * @param independent the  independent of tho polynomial
     * @returns the polynomial as an Expression
     */
    static createPolynomialExpression(degree, prefix, postfix, independent) {
        if (degree < 0)
            throw new Error(`The degree of the desired polynomial cannot be lower then zero. Given degree is ${degree}`);
        const terms = [];
        for (let i = 0; i <= degree; i++) {
            const independents = [Independent.g(`${prefix}${i}${postfix}`)];
            if (i > 0) {
                independents.push(Independent.g(independent, i));
            }
            terms.push(Term.g(independents));
        }
        return TermSummationExpression.g(terms);
    }
    /**
     * Finds the value for the independent where polynomials X and Y are equal
     * @param polynomialX the X polynomial (of the curve)
     * @param polynomialY  the Y polynomial (of the curve)
     * @param vars the values to use for the variable inside the polynomial expressions
     * @param independent  the independent to solve
     * @returns  the value of the independent where the polynomials are equal or undefined when there is no root
     */
    static solveIndependent(polynomialX, polynomialY, vars, independent) {
        //): number {
        const rootsX = CubicCubicIntersections.solveForIndependent(polynomialX, vars, independent);
        const rootsY = CubicCubicIntersections.solveForIndependent(polynomialY, vars, independent);
        const rootsCommon = setIntersection(rootsX, rootsY, Polynomial.DEFAULT_ERROR);
        return rootsCommon[0];
    }
    // private static equalsTester(
    //   a: number,
    //   tolerance: number
    // ): (b: number) => boolean {
    //   return (b: number) => Math.abs(a - b) <= tolerance;
    // }
    // /**
    //  * Finds all common elements in a and b (aka the set intersection) and returns them.
    //  * @param a set of numbers a
    //  * @param b set of numbers b
    //  * @param tolerance the tolerance to be used when comparing the elements
    //  * @returns the intersection of set a and b
    //  */
    // public static common(a: number[], b: number[], tolerance: number): number[] {
    //   const _b = b.map((v) => v); //copy b
    //   const c: number[] = a.reduce(
    //     (c, va) => {
    //       const isEQ = CubicCubicIntersections.equalsTester(va, tolerance);
    //       let ib = _b.findIndex(isEQ);
    //       if (ib != -1) {
    //         _b.splice(ib, 1);
    //         c.push(va);
    //       }
    //       return c;
    //     },
    //     <number[]>[]
    //   );
    //   return c;
    // }
    /**
     * Constructs a sylvester matrix
     * @param xt
     * @param yt
     * @param independent
     * @returns
     */
    static createSylvesterMatrix(xt, yt, independent) {
        // get the coefficients of the expressions xt and yt for a polynomial with
        // given independent.
        const pxt = xt.coefficients(independent);
        const pyt = yt.coefficients(independent);
        // the polynomial with the highest degree determines the degree to use
        const degree = Math.max(parseInt(Object.keys(pxt).at(-1)), parseInt(Object.keys(pyt).at(-1)));
        // make sure that both polynomials have the same amount of coefficients (append
        // with zeros, i.e. empty expressions)
        for (let i = 0; i < degree + 1; i++) {
            const _i = i.toString();
            if (pxt[_i] == null) {
                pxt[_i] = TermSummationExpression.g();
            }
            if (pyt[_i] == null) {
                pyt[_i] = TermSummationExpression.g();
            }
        }
        // the size of the Sylvester matrix is 2 * degree
        const size = degree * 2;
        // create such a matrix with empty expressions
        const matrix = [];
        while (matrix.length < size) {
            const row = [];
            while (row.length < size) {
                row.push(TermSummationExpression.g());
            }
            matrix.push(row);
        }
        // now distribute the coefficients over the matrix
        for (let offset = 0; offset < degree; offset++) {
            const xRow = matrix[offset];
            const yRow = matrix[offset + degree];
            for (let power = degree; power >= 0; power--) {
                const _power = power.toString();
                let col = offset + degree - power;
                xRow[col] = pxt[_power];
                yRow[col] = pyt[_power];
            }
        }
        return matrix;
    }
    /**
     * Returns the expression for the determinant of the given matrix of expressions
     * @param matrix The matrix of expressions
     * @returns The expression for the determinant of the given matrix
     */
    static determinant(matrix) {
        const n = matrix.length;
        let D = TermSummationExpression.g();
        if (n == 1) {
            D = matrix[0][0];
        }
        else if (n == 2) {
            D = matrix[0][0]
                .mult(matrix[1][1])
                .subtract(matrix[0][1].mult(matrix[1][0]));
        }
        else {
            for (let row = 0; row < n; row++) {
                const subM = CubicCubicIntersections.subMatrix(matrix, row, 0);
                const subD = CubicCubicIntersections.determinant(subM);
                const partialD = matrix[row][0].mult(subD);
                if (row % 2 == 0) {
                    // add
                    D = D.add(partialD);
                }
                else {
                    // subtract
                    D = D.subtract(partialD);
                }
            }
        }
        return D;
    }
    /**
     * Returns the sub-matrix by omitting the given row and column from the given matrix
     * @param matrix the matrix where to return the sub-matrix for.
     * @param row the row to omit
     * @param col the column to omit
     * @returns the sub-matrix
     */
    static subMatrix(matrix, row, col) {
        if (matrix.length > 1 &&
            matrix[0].length > 1 &&
            row < matrix.length &&
            col < matrix[0].length &&
            row >= 0 &&
            col >= 0) {
            const h = matrix.length;
            const w = matrix[0].length;
            const subMatrix = [];
            let copyRow = 0;
            while (subMatrix.length < h - 1) {
                const subMatrixRow = [];
                if (copyRow == row)
                    copyRow++;
                let copyCol = 0;
                while (subMatrixRow.length < w - 1) {
                    if (copyCol == col)
                        copyCol++;
                    subMatrixRow.push(matrix[copyRow][copyCol]);
                    copyCol++;
                }
                copyRow++;
                subMatrix.push(subMatrixRow);
            }
            return subMatrix;
        }
        else {
            throw new Error("No valid matrix or indices given");
        }
    }
    static solveForIndependent(expression, vars, independent) {
        const C = CubicCubicIntersections.getCoefficients(expression, vars, independent);
        const roots = Polynomial.rootsOpen(C);
        return roots;
    }
    static getCoefficients(expression, vars, independent) {
        const powerMap = expression.coefficients(independent);
        const powerExpressions = Object.values(powerMap);
        const roots = powerExpressions.map((powerExpression) => {
            const optimizedPowerExpression = powerExpression.optimize();
            return optimizedPowerExpression.solve(vars);
        });
        return roots;
    }
    static getScalers(curve0, curve1) {
        let yBounds0 = CubicBezier.yControlBounds(curve0);
        let yBounds1 = CubicBezier.yControlBounds(curve1);
        let overlap = yBounds0.max.y >= yBounds1.min.y && yBounds0.min.y <= yBounds1.max.y;
        if (overlap) {
            yBounds0 = CubicBezier.yBounds(curve0);
            yBounds1 = CubicBezier.yBounds(curve1);
            overlap =
                yBounds0.max.y >= yBounds1.min.y && yBounds0.min.y <= yBounds1.max.y;
            if (overlap) {
                let xBounds0 = CubicBezier.xControlBounds(curve0);
                let xBounds1 = CubicBezier.xControlBounds(curve1);
                overlap =
                    xBounds0.max.x >= xBounds1.min.x && xBounds0.min.x <= xBounds1.max.x;
                if (overlap) {
                    xBounds0 = CubicBezier.xBounds(curve0);
                    xBounds1 = CubicBezier.xBounds(curve1);
                    overlap =
                        xBounds0.max.x >= xBounds1.min.x &&
                            xBounds0.min.x <= xBounds1.max.x;
                    if (overlap) {
                        //there is overlap
                        //build scalers
                        const b = {
                            min: {
                                x: Math.min(xBounds0.min.x, xBounds1.min.x),
                                y: Math.min(yBounds0.min.y, yBounds1.min.y),
                            },
                            max: {
                                x: Math.max(xBounds0.max.x, xBounds1.max.x),
                                y: Math.max(yBounds0.max.y, yBounds1.max.y),
                            },
                        };
                        const xRange = b.max.x - b.min.x;
                        const yRange = b.max.y - b.min.y;
                        const xGain = xRange < 100.0 ? 1.0 : 100.0 / xRange;
                        const yGain = yRange < 100.0 ? 1.0 : 100.0 / yRange;
                        const xScale = (x) => (x - b.min.x) * xGain;
                        const yScale = (y) => (y - b.min.y) * yGain;
                        return { xScale, yScale };
                    }
                }
            }
        }
        //no overlap
        return undefined;
    }
}
class Degree3 {
    aX;
    aY;
    bX;
    bY;
    akX;
    akY;
    bkX;
    bkY;
    akXMinusBX;
    akYMinusBY;
    bkXMinusAX;
    bkYMinusAY;
    sylvesterM;
    sylMDet;
    constructor() {
        // polynomial expression of curve a X(t)
        this.aX = CubicCubicIntersections.createPolynomialExpression(3, "a", "x", "t");
        // polynomial expression of curve a Y(t)
        this.aY = CubicCubicIntersections.createPolynomialExpression(3, "a", "y", "t");
        // polynomial expression of curve b X(t)
        this.bX = CubicCubicIntersections.createPolynomialExpression(3, "b", "x", "t");
        // polynomial expression of curve b Y(t)
        this.bY = CubicCubicIntersections.createPolynomialExpression(3, "b", "y", "t");
        // multiply each term in curve a at position i with k^i
        // this is called the shortening or lengthening of curve a so it fits curve b
        this.akX = this.aX.toPolynomial("k");
        this.akY = this.aY.toPolynomial("k");
        // multiply each term in curve b at position i with k^i
        // this is called the shortening or lengthening of curve b so it fits curve a
        this.bkX = this.bX.toPolynomial("k");
        this.bkY = this.bY.toPolynomial("k");
        // subtract curve b from curve ak
        this.akXMinusBX = this.akX.subtract(this.bX);
        this.akYMinusBY = this.akY.subtract(this.bY);
        // subtract curve a from curve bk
        this.bkXMinusAX = this.bkX.subtract(this.aX);
        this.bkYMinusAY = this.bkY.subtract(this.aY);
        // construct the Sylvester matrix for curve bk-a
        this.sylvesterM = CubicCubicIntersections.createSylvesterMatrix(this.bkXMinusAX, this.bkYMinusAY, "t");
        // construct the expression for the determinant of this Sylvester matrix
        this.sylMDet = CubicCubicIntersections.determinant(this.sylvesterM); //this is the one we are after
    }
    logExpressions(vars) {
        console.log("aX : ", this.aX.toString());
        console.log("aY : ", this.aY.toString());
        console.log("bX : ", this.bX.toString());
        console.log("bY : ", this.bY.toString());
        console.log("vars : ", vars);
        const sylvesterX = this.bkXMinusAX.coefficients("t");
        const sylvesterY = this.bkYMinusAY.coefficients("t");
        console.log("sylvesterX");
        console.log(this.coefficientsToString(sylvesterX, "t"));
        console.log("sylvesterY");
        console.log(this.coefficientsToString(sylvesterY, "t"));
        console.log(`det=${this.sylMDet.toString()}`);
        let sb = "det";
        sb += " :\n";
        const histogram = this.sylMDet.getHistogram();
        histogram.forEach((entry) => (sb += `  ${entry.key} : ${entry.frequency} : ${entry.independent.toString(true, false)} \n`));
        console.log(sb);
        const opHistogram = D3.sylMDet.getOperationsHistogram();
        sb = "det operations : ";
        Object.keys(opHistogram).forEach((opKey) => (sb += `  ${opKey}: ${opHistogram[opKey]}`));
        console.log(sb);
        const polynomialInK = D3.sylMDet.coefficients("k");
        console.log("polynomial det(k)");
        console.log(this.coefficientsToString(polynomialInK, "k", vars));
    }
    coefficientsToString(powerMap, independentName, vars) {
        let sb = "";
        Object.keys(powerMap).forEach((power) => {
            const powerExpression = powerMap[power].optimize();
            sb += independentName;
            sb += toSuper(parseInt(power));
            sb += " : ";
            if (vars != null) {
                sb += "(";
                sb += powerExpression.solve(vars);
                sb += ") ";
            }
            sb += powerExpression.toSourceCode();
            const opHistogram = powerExpression.getOperationsHistogram();
            sb += "\n";
            sb += independentName;
            sb += toSuper(parseInt(power));
            sb += " ops : ";
            Object.keys(opHistogram).forEach((opKey) => (sb += `  ${opKey}: ${opHistogram[opKey]}`));
            sb += "\n";
            sb += independentName;
            sb += toSuper(parseInt(power));
            sb += " hist :\n";
            const histogram = powerMap[power].getHistogram();
            histogram.forEach((entry) => (sb += `  ${entry.key} : ${entry.frequency} : ${entry.independent.toString(true, false)} \n`));
            sb += "\n";
        });
        return sb;
    }
    getTCoefficientsAKXMinusBX = (vars) => {
        const a0x = vars["a0x"];
        const a1x = vars["a1x"];
        const a2x = vars["a2x"];
        const a3x = vars["a3x"];
        const b0x = vars["b0x"];
        const b1x = vars["b1x"];
        const b2x = vars["b2x"];
        const b3x = vars["b3x"];
        const k = vars["k"];
        const k_p2 = vars["k_p2"];
        const k_p3 = vars["k_p3"];
        return [
            (a0x - b0x),
            (a1x * k - b1x),
            (k_p2 * a2x - b2x),
            (k_p3 * a3x - b3x)
        ];
    };
    getTCoefficientsAKYMinusBY = (vars) => {
        const a0y = vars["a0y"];
        const a1y = vars["a1y"];
        const a2y = vars["a2y"];
        const a3y = vars["a3y"];
        const b0y = vars["b0y"];
        const b1y = vars["b1y"];
        const b2y = vars["b2y"];
        const b3y = vars["b3y"];
        const k = vars["k"];
        const k_p2 = vars["k_p2"];
        const k_p3 = vars["k_p3"];
        return [
            (a0y - b0y),
            (a1y * k - b1y),
            (k_p2 * a2y - b2y),
            (k_p3 * a3y - b3y)
        ];
    };
    getTCoefficientsBKXMinusAX = (vars) => {
        const a0x = vars["a0x"];
        const a1x = vars["a1x"];
        const a2x = vars["a2x"];
        const a3x = vars["a3x"];
        const b0x = vars["b0x"];
        const b1x = vars["b1x"];
        const b2x = vars["b2x"];
        const b3x = vars["b3x"];
        const k = vars["k"];
        const k_p2 = vars["k_p2"];
        const k_p3 = vars["k_p3"];
        return [
            (b0x - a0x),
            (b1x * k - a1x),
            (k_p2 * b2x - a2x),
            (k_p3 * b3x - a3x)
        ];
    };
    getTCoefficientsBKYMinusAY = (vars) => {
        const a0y = vars["a0y"];
        const a1y = vars["a1y"];
        const a2y = vars["a2y"];
        const a3y = vars["a3y"];
        const b0y = vars["b0y"];
        const b1y = vars["b1y"];
        const b2y = vars["b2y"];
        const b3y = vars["b3y"];
        const k = vars["k"];
        const k_p2 = vars["k_p2"];
        const k_p3 = vars["k_p3"];
        return [
            (b0y - a0y),
            (b1y * k - a1y),
            (k_p2 * b2y - a2y),
            (k_p3 * b3y - a3y)
        ];
    };
    getKCoefficients = (vars) => {
        const a0x = vars["a0x"];
        const a0y = vars["a0y"];
        const a1x = vars["a1x"];
        const a1y = vars["a1y"];
        const a2x = vars["a2x"];
        const a2y = vars["a2y"];
        const a3x = vars["a3x"];
        const a3y = vars["a3y"];
        const b0x = vars["b0x"];
        const b0y = vars["b0y"];
        const b1x = vars["b1x"];
        const b1y = vars["b1y"];
        const b2x = vars["b2x"];
        const b2y = vars["b2y"];
        const b3x = vars["b3x"];
        const b3y = vars["b3y"];
        const a0x_p2 = vars["a0x_p2"];
        const a0y_p2 = vars["a0y_p2"];
        const a1x_p2 = vars["a1x_p2"];
        const a1y_p2 = vars["a1y_p2"];
        const a2x_p2 = vars["a2x_p2"];
        const a2y_p2 = vars["a2y_p2"];
        const a3x_p2 = vars["a3x_p2"];
        const a3y_p2 = vars["a3y_p2"];
        const b0x_p2 = vars["b0x_p2"];
        const b0y_p2 = vars["b0y_p2"];
        const b1x_p2 = vars["b1x_p2"];
        const b1y_p2 = vars["b1y_p2"];
        const b2x_p2 = vars["b2x_p2"];
        const b2y_p2 = vars["b2y_p2"];
        const b3x_p2 = vars["b3x_p2"];
        const b3y_p2 = vars["b3y_p2"];
        const a0x_p3 = vars["a0x_p3"];
        const a0y_p3 = vars["a0y_p3"];
        const a1x_p3 = vars["a1x_p3"];
        const a1y_p3 = vars["a1y_p3"];
        const a2x_p3 = vars["a2x_p3"];
        const a2y_p3 = vars["a2y_p3"];
        const a3x_p3 = vars["a3x_p3"];
        const a3y_p3 = vars["a3y_p3"];
        const b0x_p3 = vars["b0x_p3"];
        const b0y_p3 = vars["b0y_p3"];
        const b1x_p3 = vars["b1x_p3"];
        const b1y_p3 = vars["b1y_p3"];
        const b2x_p3 = vars["b2x_p3"];
        const b2y_p3 = vars["b2y_p3"];
        const b3x_p3 = vars["b3x_p3"];
        const b3y_p3 = vars["b3y_p3"];
        return [
            ((a3x
                * ((a3y * ((a1x * ((a0y * (-6 * a2x * b0y + (a2y * (a0x + -b0x)))) + ((a2x * (3 * a0y_p2 + 3 * b0y_p2))
                    + ((a2y * (b0y * (-a0x + b0x))) + (a1y_p2 * (2 * a0x + -2 * b0x))))))
                    + (a1y * ((a0x * ((a2x * (-a0y + b0y)) + 6 * a2y * b0x)) + ((a2x * (b0x * (a0y + -b0y)))
                        + ((a2y * (-3 * a0x_p2 + -3 * b0x_p2)) + (a1x_p2 * (-2 * a0y + 2 * b0y))))))))
                    + ((a3y_p2 * ((a0x * (b0x * (-6 * a0y + 6 * b0y)))
                        + ((a0x_p2 * (3 * a0y + -3 * b0y)) + (b0x_p2 * (3 * a0y + -3 * b0y)))))
                        + ((a2x * ((a2y_p2 * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y))))
                            + (a2y * ((a1x * (a1y * (b0y - a0y))) + (a1y_p2 * (-b0x + a0x))))))
                            + ((a2x_p2 * (a2y * (-2 * a0y * b0y + (b0y_p2 + a0y_p2))))
                                + ((a2y_p2 * ((a1x * (a1y * (b0x - a0x))) + (a1x_p2 * (-b0y + a0y))))
                                    + (a2y_p3 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2)))))))))
                + ((a3x_p2 * ((a0y * ((b0y * ((a3y * (6 * a0x + -6 * b0x)) + (4 * a1x * a2y + 2 * a1y * a2x)))
                    + ((a1y * (a2y * (3 * a0x + -3 * b0x))) + a1y_p2 * a1x)))
                    + ((a3y * ((a0x * (-3 * a0y_p2 + -3 * b0y_p2)) + (b0x * (3 * a0y_p2 + 3 * b0y_p2))))
                        + ((a2y * ((a1y * (b0y * (-3 * a0x + 3 * b0x))) + (a1x * (-2 * a0y_p2 + -2 * b0y_p2))))
                            + ((a1y * (a2x * (-b0y_p2 - a0y_p2)))
                                + ((a1y_p3 * (b0x - a0x)) + -a1y_p2 * a1x * b0y))))))
                    + ((a2x * ((a3y_p2 * ((a1x * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y))))
                        + (a1y * (-4 * a0x * b0x + (2 * a0x_p2 + 2 * b0x_p2)))))
                        + (a3y * ((a2y * ((a1x * (a1y * (-b0x + a0x))) + (a1x_p2 * (b0y - a0y))))
                            + (a2y_p2 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2)))))))
                        + ((a3y * ((a2x_p2
                            * ((a2y * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y))))
                                + ((a1x * (a1y * (-b0y + a0y))) + (a1y_p2 * (b0x - a0x)))))
                            + (a2x_p3 * (2 * a0y * b0y + (-b0y_p2 - a0y_p2)))))
                            + ((a3x_p3 * (3 * b0y_p2 * a0y + (-3 * a0y_p2 * b0y + (-b0y_p3 + a0y_p3))))
                                + ((a3y_p2 * ((a1x * (a2y * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))
                                    + ((a1x_p2 * (a1y * (b0x - a0x))) + (a1x_p3 * (-b0y + a0y)))))
                                    + (a3y_p3 * (-3 * b0x_p2 * a0x
                                        + (3 * a0x_p2 * b0x + (b0x_p3 - a0x_p3)))))))))),
            ((a3x * ((a3y * ((b1x * ((a0y * (6 * a2x * b0y + ((a2y * (-a0x + b0x)) + 4 * a1x * a1y))) + ((b0y * ((a2y * (a0x + -b0x)) + -4 * a1x * a1y)) + ((a2x * (-3 * a0y_p2 + -3 * b0y_p2)) + (a1y_p2 * (-2 * a0x + 2 * b0x)))))) + (b1y * ((a0x * ((a2x * (a0y + -b0y)) + (-6 * a2y * b0x + -4 * a1x * a1y))) + ((b0x * ((a2x * (-a0y + b0y)) + 4 * a1x * a1y)) + ((a2y * (3 * a0x_p2 + 3 * b0x_p2)) + (a1x_p2 * (2 * a0y + -2 * b0y)))))))) + ((a1x * ((a2y_p2 * ((b1x * (-2 * a0y + 2 * b0y)) + (b1y * (-b0x + a0x)))) + (a2x * (a2y * (b1y * (-b0y + a0y)))))) + (a1y * ((a2x * (a2y * ((b1y * (-2 * a0x + 2 * b0x)) + (b1x * (-b0y + a0y))))) + (a2y_p2 * (b1x * (-b0x + a0x)))))))) + ((b1x * ((a3y_p2 * ((a2x * ((a0x * (3 * a0y + -3 * b0y)) + (b0x * (-3 * a0y + 3 * b0y)))) + ((a1x_p2 * (-3 * a0y + 3 * b0y)) + ((a0x * (2 * a1x * a1y + 2 * a2y * b0x)) + (-2 * a1x * a1y * b0x + (a2y * (-b0x_p2 - a0x_p2))))))) + ((a2y * ((a3x_p2 * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (a2x * (a3y * ((a1x * (2 * a0y + -2 * b0y)) + (a1y * (b0x - a0x))))))) + ((a0y * (-a1y_p2 * a3x_p2 - a2x_p2 * a1y * a3y)) + (b0y * (a1y_p2 * a3x_p2 + a2x_p2 * a1y * a3y)))))) + (b1y * ((a3x_p2 * ((a2y * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y)))) + ((a1y_p2 * (3 * a0x + -3 * b0x)) + ((a0y * (-2 * a1x * a1y + -2 * a2x * b0y)) + (2 * a1x * a1y * b0y + (a2x * (b0y_p2 + a0y_p2))))))) + ((a2x * ((a3y_p2 * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (a1x * (a2y * (a3y * (b0x - a0x)))))) + ((a2x_p2 * (a3y * ((a1y * (2 * a0x + -2 * b0x)) + (a1x * (b0y - a0y))))) + (a1x_p2 * (a3y_p2 * (-b0x + a0x))))))))),
            ((a3x * ((a3y * ((a1x * ((a0y * (6 * b0y * b2x + ((b2y * (-a0x + b0x)) + -4 * b1x * b1y))) + ((b0y * ((b2y * (a0x + -b0x)) + 4 * b1x * b1y)) + ((b2x * (-3 * a0y_p2 + -3 * b0y_p2)) + (b1y_p2 * (2 * a0x + -2 * b0x)))))) + (a1y * ((a0x * (-6 * b0x * b2y + ((b2x * (a0y + -b0y)) + 4 * b1x * b1y))) + ((b0x * ((b2x * (-a0y + b0y)) + -4 * b1x * b1y)) + ((b2y * (3 * a0x_p2 + 3 * b0x_p2)) + (b1x_p2 * (-2 * a0y + 2 * b0y)))))))) + ((b2y * ((a2y * ((a2x * ((a0x * (4 * a0y + -4 * b0y)) + (b0x * (-4 * a0y + 4 * b0y)))) + ((a1x * (a1y * (2 * a0x + -2 * b0x))) + (a1x_p2 * (-2 * a0y + 2 * b0y))))) + ((a2y_p2 * (6 * a0x * b0x + (-3 * a0x_p2 + -3 * b0x_p2))) + ((a2x * ((a1x * (a1y * (-b0y + a0y))) + (a1y_p2 * (b0x - a0x)))) + (a2x_p2 * (2 * a0y * b0y + (-b0y_p2 - a0y_p2))))))) + ((b2x * ((a2y * ((a2x * (4 * a0y * b0y + (-2 * a0y_p2 + -2 * b0y_p2))) + ((a1x * (a1y * (-b0y + a0y))) + (a1y_p2 * (b0x - a0x))))) + (a2y_p2 * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y)))))) + ((a2x * (a2y * ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x))))) + (a2y_p2 * ((b1x * (b1y * (b0x - a0x))) + (b1x_p2 * (-b0y + a0y))))))))) + ((a3y * ((b2x * ((a2x * ((a2y * ((a0x * (-4 * a0y + 4 * b0y)) + (b0x * (4 * a0y + -4 * b0y)))) + ((a1x * (a1y * (-2 * a0y + 2 * b0y))) + (a1y_p2 * (2 * a0x + -2 * b0x))))) + ((a2x_p2 * (-6 * a0y * b0y + (3 * a0y_p2 + 3 * b0y_p2))) + ((a2y * ((a1x * (a1y * (b0x - a0x))) + (a1x_p2 * (-b0y + a0y)))) + (a2y_p2 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))))) + ((b2y * ((a2x * ((a2y * (-4 * a0x * b0x + (2 * a0x_p2 + 2 * b0x_p2))) + ((a1x * (a1y * (b0x - a0x))) + (a1x_p2 * (-b0y + a0y))))) + (a2x_p2 * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y)))))) + ((a2x * (a2y * ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y))))) + (a2x_p2 * ((b1x * (b1y * (-b0y + a0y))) + (b1y_p2 * (b0x - a0x)))))))) + ((a1x * ((a3y_p2 * ((b2x * ((a0x * (3 * a0y + -3 * b0y)) + (b0x * (-3 * a0y + 3 * b0y)))) + ((b1x_p2 * (3 * a0y + -3 * b0y)) + ((a0x * (2 * b0x * b2y + -2 * b1x * b1y)) + (2 * b0x * b1x * b1y + (b2y * (-b0x_p2 - a0x_p2))))))) + (a3x_p2 * ((b2y * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (b1y_p2 * (-b0y + a0y)))))) + (a1y * ((a3x_p2 * ((b2y * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y)))) + ((b1y_p2 * (-3 * a0x + 3 * b0x)) + ((a0y * (-2 * b0y * b2x + 2 * b1x * b1y)) + (-2 * b0y * b1x * b1y + (b2x * (b0y_p2 + a0y_p2))))))) + (a3y_p2 * ((b2x * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (b1x_p2 * (b0x - a0x))))))))),
            ((a3x * ((a3y * ((a0x * ((a0y * (12 * b0x * b3y + (-12 * b0y * b3x + (b1x * b2y + -b1y * b2x)))) + ((b0x * (-12 * b0y * b3y + 6 * b1y * b2y)) + ((b3x * (6 * a0y_p2 + 6 * b0y_p2)) + ((b0y * (-b1x * b2y + b1y * b2x)) + -2 * b1y_p2 * b1x))))) + ((a0y * ((b0x * (12 * b0y * b3x + (-b1x * b2y + b1y * b2x))) + ((b3y * (-6 * a0x_p2 + -6 * b0x_p2)) + (-6 * b0y * b1x * b2x + 2 * b1x_p2 * b1y)))) + ((b0x * ((b3x * (-6 * a0y_p2 + -6 * b0y_p2)) + ((b0y * (b1x * b2y + -b1y * b2x)) + 2 * b1y_p2 * b1x))) + ((b0y * ((b3y * (6 * a0x_p2 + 6 * b0x_p2)) + -2 * b1x_p2 * b1y)) + ((b1x * (b2x * (3 * a0y_p2 + 3 * b0y_p2))) + (b1y * (b2y * (-3 * a0x_p2 + -3 * b0x_p2))))))))) + ((a2y * ((a1y * ((b3x * ((a0x * (-6 * a0y + 6 * b0y)) + (b0x * (6 * a0y + -6 * b0y)))) + ((b3y * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + ((b1x * ((b2y * (-2 * a0x + 2 * b0x)) + (b2x * (b0y - a0y)))) + (b1y * (b2x * (2 * a0x + -2 * b0x))))))) + (a1x * ((a0y * (-8 * b0y * b3x + ((b3y * (-a0x + b0x)) + (4 * b1x * b2y + -b1y * b2x)))) + ((b0y * ((b3y * (a0x + -b0x)) + (-4 * b1x * b2y + b1y * b2x))) + ((b3x * (4 * a0y_p2 + 4 * b0y_p2)) + (b1y * (b2y * (-2 * a0x + 2 * b0x))))))))) + ((a2x * ((a1y * ((b3y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))) + ((b3x * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (b2y * ((b1y * (2 * a0x + -2 * b0x)) + (b1x * (b0y - a0y))))))) + (a1x * ((b3y * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + (b1y * (b2y * (b0y - a0y))))))) + ((a1x * (a1y_p2 * ((b3x * (-2 * a0y + 2 * b0y)) + (b3y * (-2 * a0x + 2 * b0x))))) + ((a1x_p2 * (a1y * (b3y * (2 * a0y + -2 * b0y)))) + (a1y_p3 * (b3x * (2 * a0x + -2 * b0x))))))))) + ((a3y * ((a2x * ((a1x * ((b3y * ((a0x * (6 * a0y + -6 * b0y)) + (b0x * (-6 * a0y + 6 * b0y)))) + ((b3x * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + ((b1y * ((b2x * (2 * a0y + -2 * b0y)) + (b2y * (-b0x + a0x)))) + (b1x * (b2y * (-2 * a0y + 2 * b0y))))))) + (a1y * ((a0x * (8 * b0x * b3y + ((b3x * (a0y + -b0y)) + (-4 * b1y * b2x + b1x * b2y)))) + ((b0x * ((b3x * (-a0y + b0y)) + (4 * b1y * b2x + -b1x * b2y))) + ((b3y * (-4 * a0x_p2 + -4 * b0x_p2)) + (b1x * (b2x * (2 * a0y + -2 * b0y))))))))) + ((a2y * ((a1x * ((b3x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))) + ((b3y * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (b2x * ((b1x * (-2 * a0y + 2 * b0y)) + (b1y * (-b0x + a0x))))))) + (a1y * ((b3x * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + (b1x * (b2x * (-b0x + a0x))))))) + ((a1x_p2 * (a1y * ((b3x * (2 * a0y + -2 * b0y)) + (b3y * (2 * a0x + -2 * b0x))))) + ((a1x * (a1y_p2 * (b3x * (-2 * a0x + 2 * b0x)))) + (a1x_p3 * (b3y * (-2 * a0y + 2 * b0y)))))))) + ((a3x_p2 * ((a0y * ((b0y * ((b3y * (-6 * a0x + 6 * b0x)) + (4 * b1x * b2y + 2 * b1y * b2x))) + (-9 * b0y_p2 * b3x + ((b1y * (b2y * (3 * a0x + -3 * b0x))) + -b1y_p2 * b1x)))) + ((a0y_p2 * (9 * b0y * b3x + ((b3y * (3 * a0x + -3 * b0x)) + (-2 * b1x * b2y + -b1y * b2x)))) + ((b0y_p2 * ((b3y * (3 * a0x + -3 * b0x)) + (-2 * b1x * b2y + -b1y * b2x))) + ((b0y * ((b1y * (b2y * (-3 * a0x + 3 * b0x))) + b1y_p2 * b1x)) + ((b3x * (-3 * a0y_p3 + 3 * b0y_p3)) + (b1y_p3 * (-b0x + a0x)))))))) + ((a3y_p2 * ((a0x * ((b0x * ((b3x * (6 * a0y + -6 * b0y)) + (-4 * b1y * b2x + -2 * b1x * b2y))) + (9 * b0x_p2 * b3y + ((b1x * (b2x * (-3 * a0y + 3 * b0y))) + b1x_p2 * b1y)))) + ((a0x_p2 * (-9 * b0x * b3y + ((b3x * (-3 * a0y + 3 * b0y)) + (2 * b1y * b2x + b1x * b2y)))) + ((b0x_p2 * ((b3x * (-3 * a0y + 3 * b0y)) + (2 * b1y * b2x + b1x * b2y))) + ((b0x * ((b1x * (b2x * (3 * a0y + -3 * b0y))) + -b1x_p2 * b1y)) + ((b3y * (3 * a0x_p3 + -3 * b0x_p3)) + (b1x_p3 * (b0y - a0y)))))))) + ((b3x * ((a2x * ((a2y_p2 * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y)))) + (a2y * ((a1x * (a1y * (-b0y + a0y))) + (a1y_p2 * (b0x - a0x)))))) + ((a2x_p2 * (a2y * (2 * a0y * b0y + (-b0y_p2 - a0y_p2)))) + ((a2y_p2 * ((a1x * (a1y * (-b0x + a0x))) + (a1x_p2 * (b0y - a0y)))) + (a2y_p3 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2))))))) + (b3y * ((a2x_p2 * ((a2y * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y)))) + ((a1x * (a1y * (b0y - a0y))) + (a1y_p2 * (-b0x + a0x))))) + ((a2x * ((a2y * ((a1x * (a1y * (b0x - a0x))) + (a1x_p2 * (-b0y + a0y)))) + (a2y_p2 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))) + (a2x_p3 * (-2 * a0y * b0y + (b0y_p2 + a0y_p2))))))))))),
            ((a3x * ((a2y * ((b1y * ((b3x * ((a0x * (6 * a0y + -6 * b0y)) + (b0x * (-6 * a0y + 6 * b0y)))) + ((b3y * (6 * a0x * b0x + (-3 * a0x_p2 + -3 * b0x_p2))) + (b1x * ((b2y * (2 * a0x + -2 * b0x)) + (b2x * (-b0y + a0y))))))) + ((b1x * ((b3x * (8 * a0y * b0y + (-4 * a0y_p2 + -4 * b0y_p2))) + (b3y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))))) + ((b2y * ((b2x * ((a0x * (-4 * a0y + 4 * b0y)) + (b0x * (4 * a0y + -4 * b0y)))) + (b1x_p2 * (-2 * a0y + 2 * b0y)))) + ((b2y_p2 * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + ((b2x_p2 * (-2 * a0y * b0y + (b0y_p2 + a0y_p2))) + (b1y_p2 * (b2x * (b0x - a0x))))))))) + ((b1y * ((b3x * ((a1y_p2 * (-6 * a0x + 6 * b0x)) + ((a0y * (4 * a1x * a1y + 4 * a2x * b0y)) + (-4 * a1x * a1y * b0y + (a2x * (-2 * a0y_p2 + -2 * b0y_p2)))))) + ((b3y * ((a2x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))) + ((a1x * (a1y * (4 * a0x + -4 * b0x))) + (a1x_p2 * (-2 * a0y + 2 * b0y))))) + (a2x * (b1x * (b2y * (-b0y + a0y))))))) + ((a2x * ((a0y * ((b0y * (-6 * b1x * b3y + -4 * b2x * b2y)) + (b2y_p2 * (-2 * a0x + 2 * b0x)))) + ((b1x * (b3y * (3 * a0y_p2 + 3 * b0y_p2))) + ((b2y * ((b2x * (2 * a0y_p2 + 2 * b0y_p2)) + (b1y_p2 * (b0x - a0x)))) + (b0y * (b2y_p2 * (2 * a0x + -2 * b0x))))))) + ((b1x * ((b3y * ((a1x * (a1y * (-4 * a0y + 4 * b0y))) + (a1y_p2 * (2 * a0x + -2 * b0x)))) + (a1y_p2 * (b3x * (2 * a0y + -2 * b0y))))) + ((a1x * (a1y * ((b2x * (b2y * (b0y - a0y))) + (b2y_p2 * (b0x - a0x))))) + ((a1x_p2 * (b2y_p2 * (-b0y + a0y))) + (a1y_p2 * (b2x * (b2y * (-b0x + a0x))))))))))) + ((a3y * ((a2x * ((b1x * ((b3y * ((a0x * (-6 * a0y + 6 * b0y)) + (b0x * (6 * a0y + -6 * b0y)))) + ((b3x * (-6 * a0y * b0y + (3 * a0y_p2 + 3 * b0y_p2))) + (b1y * ((b2x * (-2 * a0y + 2 * b0y)) + (b2y * (b0x - a0x))))))) + ((b1y * ((b3y * (-8 * a0x * b0x + (4 * a0x_p2 + 4 * b0x_p2))) + (b3x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))))) + ((b2x * ((b2y * ((a0x * (4 * a0y + -4 * b0y)) + (b0x * (-4 * a0y + 4 * b0y)))) + (b1y_p2 * (2 * a0x + -2 * b0x)))) + ((b2x_p2 * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + ((b2y_p2 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2))) + (b1x_p2 * (b2y * (-b0y + a0y))))))))) + ((b1x * ((b3y * ((a1x_p2 * (6 * a0y + -6 * b0y)) + ((a0x * (-4 * a1x * a1y + -4 * a2y * b0x)) + (4 * a1x * a1y * b0x + (a2y * (2 * a0x_p2 + 2 * b0x_p2)))))) + ((b3x * ((a2y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))) + ((a1x * (a1y * (-4 * a0y + 4 * b0y))) + (a1y_p2 * (2 * a0x + -2 * b0x))))) + (a2y * (b1y * (b2x * (b0x - a0x))))))) + ((a2y * ((a0x * ((b0x * (6 * b1y * b3x + 4 * b2x * b2y)) + (b2x_p2 * (2 * a0y + -2 * b0y)))) + ((b1y * (b3x * (-3 * a0x_p2 + -3 * b0x_p2))) + ((b2x * ((b2y * (-2 * a0x_p2 + -2 * b0x_p2)) + (b1x_p2 * (-b0y + a0y)))) + (b0x * (b2x_p2 * (-2 * a0y + 2 * b0y))))))) + ((b1y * ((b3x * ((a1x * (a1y * (4 * a0x + -4 * b0x))) + (a1x_p2 * (-2 * a0y + 2 * b0y)))) + (a1x_p2 * (b3y * (-2 * a0x + 2 * b0x))))) + ((a1x * (a1y * ((b2x * (b2y * (-b0x + a0x))) + (b2x_p2 * (-b0y + a0y))))) + ((a1x_p2 * (b2x * (b2y * (b0y - a0y)))) + (a1y_p2 * (b2x_p2 * (b0x - a0x)))))))))) + ((a1x * ((a2x * (a2y * ((b3y * ((b1x * (-2 * a0y + 2 * b0y)) + (b1y * (-b0x + a0x)))) + (b1y * (b3x * (b0y - a0y)))))) + ((a2y_p2 * (b3x * ((b1x * (2 * a0y + -2 * b0y)) + (b1y * (b0x - a0x))))) + (a2x_p2 * (b1y * (b3y * (-b0y + a0y))))))) + (a1y * ((a2x * (a2y * ((b3x * ((b1y * (2 * a0x + -2 * b0x)) + (b1x * (b0y - a0y)))) + (b1x * (b3y * (-b0x + a0x)))))) + ((a2x_p2 * (b3y * ((b1y * (-2 * a0x + 2 * b0x)) + (b1x * (-b0y + a0y))))) + (a2y_p2 * (b1x * (b3x * (b0x - a0x)))))))))),
            ((b3x * ((b2y * ((a1y * ((a3x * ((a0x * (6 * a0y + -6 * b0y)) + (b0x * (-6 * a0y + 6 * b0y)))) + ((a3y * (6 * a0x * b0x + (-3 * a0x_p2 + -3 * b0x_p2))) + (a1x * ((a2y * (-2 * a0x + 2 * b0x)) + (a2x * (b0y - a0y))))))) + ((a1x * ((a3x * (8 * a0y * b0y + (-4 * a0y_p2 + -4 * b0y_p2))) + (a3y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))))) + ((a2y * ((a2x * ((a0x * (-4 * a0y + 4 * b0y)) + (b0x * (4 * a0y + -4 * b0y)))) + (a1x_p2 * (2 * a0y + -2 * b0y)))) + ((a2y_p2 * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + ((a2x_p2 * (-2 * a0y * b0y + (b0y_p2 + a0y_p2))) + (a1y_p2 * (a2x * (-b0x + a0x))))))))) + ((a1y * ((a3x * ((b1y_p2 * (6 * a0x + -6 * b0x)) + ((a0y * (4 * b0y * b2x + -4 * b1x * b1y)) + (4 * b0y * b1x * b1y + (b2x * (-2 * a0y_p2 + -2 * b0y_p2)))))) + ((a3y * ((b2x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))) + ((b1x * (b1y * (-4 * a0x + 4 * b0x))) + (b1x_p2 * (2 * a0y + -2 * b0y))))) + (a1x * (a2y * (b2x * (b0y - a0y))))))) + ((b2x * ((a0y * ((b0y * (-6 * a1x * a3y + -4 * a2x * a2y)) + (a2y_p2 * (-2 * a0x + 2 * b0x)))) + ((a1x * (a3y * (3 * a0y_p2 + 3 * b0y_p2))) + ((a2y * ((a2x * (2 * a0y_p2 + 2 * b0y_p2)) + (a1y_p2 * (-b0x + a0x)))) + (a2y_p2 * (b0y * (2 * a0x + -2 * b0x))))))) + ((a1x * ((a3y * ((b1x * (b1y * (4 * a0y + -4 * b0y))) + (b1y_p2 * (-2 * a0x + 2 * b0x)))) + (a3x * (b1y_p2 * (-2 * a0y + 2 * b0y))))) + ((a2x * (a2y * ((b1x * (b1y * (-b0y + a0y))) + (b1y_p2 * (b0x - a0x))))) + (a2y_p2 * ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y)))))))))) + ((b3y * ((b2x * ((a1x * ((a3y * ((a0x * (-6 * a0y + 6 * b0y)) + (b0x * (6 * a0y + -6 * b0y)))) + ((a3x * (-6 * a0y * b0y + (3 * a0y_p2 + 3 * b0y_p2))) + (a1y * ((a2x * (2 * a0y + -2 * b0y)) + (a2y * (-b0x + a0x))))))) + ((a1y * ((a3y * (-8 * a0x * b0x + (4 * a0x_p2 + 4 * b0x_p2))) + (a3x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))))) + ((a2x * ((a2y * ((a0x * (4 * a0y + -4 * b0y)) + (b0x * (-4 * a0y + 4 * b0y)))) + (a1y_p2 * (-2 * a0x + 2 * b0x)))) + ((a2x_p2 * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + ((a2y_p2 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2))) + (a1x_p2 * (a2y * (b0y - a0y))))))))) + ((a1x * ((a3y * ((b1x_p2 * (-6 * a0y + 6 * b0y)) + ((a0x * (-4 * b0x * b2y + 4 * b1x * b1y)) + (-4 * b0x * b1x * b1y + (b2y * (2 * a0x_p2 + 2 * b0x_p2)))))) + ((a3x * ((b2y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))) + ((b1x * (b1y * (4 * a0y + -4 * b0y))) + (b1y_p2 * (-2 * a0x + 2 * b0x))))) + (a1y * (a2x * (b2y * (-b0x + a0x))))))) + ((b2y * ((a0x * ((b0x * (6 * a1y * a3x + 4 * a2x * a2y)) + (a2x_p2 * (2 * a0y + -2 * b0y)))) + ((a1y * (a3x * (-3 * a0x_p2 + -3 * b0x_p2))) + ((a2x * ((a2y * (-2 * a0x_p2 + -2 * b0x_p2)) + (a1x_p2 * (b0y - a0y)))) + (a2x_p2 * (b0x * (-2 * a0y + 2 * b0y))))))) + ((a1y * ((a3x * ((b1x * (b1y * (-4 * a0x + 4 * b0x))) + (b1x_p2 * (2 * a0y + -2 * b0y)))) + (a3y * (b1x_p2 * (2 * a0x + -2 * b0x))))) + ((a2x * (a2y * ((b1x * (b1y * (b0x - a0x))) + (b1x_p2 * (-b0y + a0y))))) + (a2x_p2 * ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x)))))))))) + ((a1x * ((a3x * ((b2y_p2 * ((b1x * (-2 * a0y + 2 * b0y)) + (b1y * (-b0x + a0x)))) + (b1y * (b2x * (b2y * (-b0y + a0y)))))) + (a3y * ((b2x * (b2y * ((b1x * (2 * a0y + -2 * b0y)) + (b1y * (b0x - a0x))))) + (b1y * (b2x_p2 * (b0y - a0y))))))) + (a1y * ((a3x * ((b2x * (b2y * ((b1y * (-2 * a0x + 2 * b0x)) + (b1x * (-b0y + a0y))))) + (b1x * (b2y_p2 * (-b0x + a0x))))) + (a3y * ((b2x_p2 * ((b1y * (2 * a0x + -2 * b0x)) + (b1x * (b0y - a0y)))) + (b1x * (b2x * (b2y * (b0x - a0x))))))))))),
            ((b3x * ((b3y * ((a0x * ((a0y * (12 * a3x * b0y + (-12 * a3y * b0x + (a1x * a2y + -a1y * a2x)))) + ((b0x * (12 * a3y * b0y + 6 * a1y * a2y)) + ((a3x * (-6 * a0y_p2 + -6 * b0y_p2)) + ((b0y * (-a1x * a2y + a1y * a2x)) + 2 * a1y_p2 * a1x))))) + ((a0y * ((b0x * (-12 * a3x * b0y + (-a1x * a2y + a1y * a2x))) + ((a3y * (6 * a0x_p2 + 6 * b0x_p2)) + (-6 * a1x * a2x * b0y + -2 * a1x_p2 * a1y)))) + ((b0x * ((a3x * (6 * a0y_p2 + 6 * b0y_p2)) + ((b0y * (a1x * a2y + -a1y * a2x)) + -2 * a1y_p2 * a1x))) + ((b0y * ((a3y * (-6 * a0x_p2 + -6 * b0x_p2)) + 2 * a1x_p2 * a1y)) + ((a1x * (a2x * (3 * a0y_p2 + 3 * b0y_p2))) + (a1y * (a2y * (-3 * a0x_p2 + -3 * b0x_p2))))))))) + ((b2y * ((b1y * ((a3x * ((a0x * (-6 * a0y + 6 * b0y)) + (b0x * (6 * a0y + -6 * b0y)))) + ((a3y * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + ((a1x * ((a2y * (2 * a0x + -2 * b0x)) + (a2x * (-b0y + a0y)))) + (a1y * (a2x * (-2 * a0x + 2 * b0x))))))) + (b1x * ((a0y * (-8 * a3x * b0y + ((a3y * (-a0x + b0x)) + (-4 * a1x * a2y + a1y * a2x)))) + ((b0y * ((a3y * (a0x + -b0x)) + (4 * a1x * a2y + -a1y * a2x))) + ((a3x * (4 * a0y_p2 + 4 * b0y_p2)) + (a1y * (a2y * (2 * a0x + -2 * b0x))))))))) + ((b2x * ((b1y * ((a3y * ((a0x * (a0y + -b0y)) + (b0x * (-a0y + b0y)))) + ((a3x * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (a2y * ((a1y * (-2 * a0x + 2 * b0x)) + (a1x * (-b0y + a0y))))))) + (b1x * ((a3y * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + (a1y * (a2y * (-b0y + a0y))))))) + ((a3x * ((b1x * (b1y_p2 * (2 * a0y + -2 * b0y))) + (b1y_p3 * (-2 * a0x + 2 * b0x)))) + (a3y * ((b1x * (b1y_p2 * (2 * a0x + -2 * b0x))) + (b1x_p2 * (b1y * (-2 * a0y + 2 * b0y)))))))))) + ((b3y * ((b2x * ((b1x * ((a3y * ((a0x * (6 * a0y + -6 * b0y)) + (b0x * (-6 * a0y + 6 * b0y)))) + ((a3x * (6 * a0y * b0y + (-3 * a0y_p2 + -3 * b0y_p2))) + ((a1y * ((a2x * (-2 * a0y + 2 * b0y)) + (a2y * (b0x - a0x)))) + (a1x * (a2y * (2 * a0y + -2 * b0y))))))) + (b1y * ((a0x * (8 * a3y * b0x + ((a3x * (a0y + -b0y)) + (4 * a1y * a2x + -a1x * a2y)))) + ((b0x * ((a3x * (-a0y + b0y)) + (-4 * a1y * a2x + a1x * a2y))) + ((a3y * (-4 * a0x_p2 + -4 * b0x_p2)) + (a1x * (a2x * (-2 * a0y + 2 * b0y))))))))) + ((b2y * ((b1x * ((a3x * ((a0x * (-a0y + b0y)) + (b0x * (a0y + -b0y)))) + ((a3y * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (a2x * ((a1x * (2 * a0y + -2 * b0y)) + (a1y * (b0x - a0x))))))) + (b1y * ((a3x * (-6 * a0x * b0x + (3 * a0x_p2 + 3 * b0x_p2))) + (a1x * (a2x * (b0x - a0x))))))) + ((a3x * ((b1x * (b1y_p2 * (2 * a0x + -2 * b0x))) + (b1x_p2 * (b1y * (-2 * a0y + 2 * b0y))))) + (a3y * ((b1x_p2 * (b1y * (-2 * a0x + 2 * b0x))) + (b1x_p3 * (2 * a0y + -2 * b0y)))))))) + ((b3x_p2 * ((a0y * ((b0y * ((a3y * (6 * a0x + -6 * b0x)) + (4 * a1x * a2y + 2 * a1y * a2x))) + (9 * b0y_p2 * a3x + ((a1y * (a2y * (3 * a0x + -3 * b0x))) + a1y_p2 * a1x)))) + ((a0y_p2 * (-9 * a3x * b0y + ((a3y * (-3 * a0x + 3 * b0x)) + (-2 * a1x * a2y + -a1y * a2x)))) + ((b0y_p2 * ((a3y * (-3 * a0x + 3 * b0x)) + (-2 * a1x * a2y + -a1y * a2x))) + ((b0y * ((a1y * (a2y * (-3 * a0x + 3 * b0x))) + -a1y_p2 * a1x)) + ((a3x * (3 * a0y_p3 + -3 * b0y_p3)) + (a1y_p3 * (b0x - a0x)))))))) + ((b3y_p2 * ((a0x * ((b0x * ((a3x * (-6 * a0y + 6 * b0y)) + (-4 * a1y * a2x + -2 * a1x * a2y))) + (-9 * b0x_p2 * a3y + ((a1x * (a2x * (-3 * a0y + 3 * b0y))) + -a1x_p2 * a1y)))) + ((a0x_p2 * (9 * a3y * b0x + ((a3x * (3 * a0y + -3 * b0y)) + (2 * a1y * a2x + a1x * a2y)))) + ((b0x_p2 * ((a3x * (3 * a0y + -3 * b0y)) + (2 * a1y * a2x + a1x * a2y))) + ((b0x * ((a1x * (a2x * (3 * a0y + -3 * b0y))) + a1x_p2 * a1y)) + ((a3y * (-3 * a0x_p3 + 3 * b0x_p3)) + (a1x_p3 * (-b0y + a0y)))))))) + ((a3x * ((b2x * ((b2y_p2 * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y)))) + (b2y * ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x)))))) + ((b2x_p2 * (b2y * (2 * a0y * b0y + (-b0y_p2 - a0y_p2)))) + ((b2y_p2 * ((b1x * (b1y * (b0x - a0x))) + (b1x_p2 * (-b0y + a0y)))) + (b2y_p3 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2))))))) + (a3y * ((b2x_p2 * ((b2y * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y)))) + ((b1x * (b1y * (-b0y + a0y))) + (b1y_p2 * (b0x - a0x))))) + ((b2x * ((b2y * ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y)))) + (b2y_p2 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))) + (b2x_p3 * (-2 * a0y * b0y + (b0y_p2 + a0y_p2))))))))))),
            ((b3x * ((b3y * ((b1x * ((a0y * (6 * a2x * b0y + ((a2y * (-a0x + b0x)) + 4 * a1x * a1y))) + ((b0y * ((a2y * (a0x + -b0x)) + -4 * a1x * a1y)) + ((a2x * (-3 * a0y_p2 + -3 * b0y_p2)) + (a1y_p2 * (-2 * a0x + 2 * b0x)))))) + (b1y * ((a0x * ((a2x * (a0y + -b0y)) + (-6 * a2y * b0x + -4 * a1x * a1y))) + ((b0x * ((a2x * (-a0y + b0y)) + 4 * a1x * a1y)) + ((a2y * (3 * a0x_p2 + 3 * b0x_p2)) + (a1x_p2 * (2 * a0y + -2 * b0y)))))))) + ((a2y * ((b2y * ((b2x * ((a0x * (4 * a0y + -4 * b0y)) + (b0x * (-4 * a0y + 4 * b0y)))) + ((b1x * (b1y * (-2 * a0x + 2 * b0x))) + (b1x_p2 * (2 * a0y + -2 * b0y))))) + ((b2y_p2 * (6 * a0x * b0x + (-3 * a0x_p2 + -3 * b0x_p2))) + ((b2x * ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x)))) + (b2x_p2 * (2 * a0y * b0y + (-b0y_p2 - a0y_p2))))))) + ((a2x * ((b2y * ((b2x * (4 * a0y * b0y + (-2 * a0y_p2 + -2 * b0y_p2))) + ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x))))) + (b2y_p2 * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y)))))) + ((a1x * (a1y * ((b2x * (b2y * (-b0y + a0y))) + (b2y_p2 * (-b0x + a0x))))) + ((a1x_p2 * (b2y_p2 * (b0y - a0y))) + (a1y_p2 * (b2x * (b2y * (b0x - a0x)))))))))) + ((b3y * ((a2x * ((b2x * ((b2y * ((a0x * (-4 * a0y + 4 * b0y)) + (b0x * (4 * a0y + -4 * b0y)))) + ((b1x * (b1y * (2 * a0y + -2 * b0y))) + (b1y_p2 * (-2 * a0x + 2 * b0x))))) + ((b2x_p2 * (-6 * a0y * b0y + (3 * a0y_p2 + 3 * b0y_p2))) + ((b2y * ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y)))) + (b2y_p2 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))))) + ((a2y * ((b2x * ((b2y * (-4 * a0x * b0x + (2 * a0x_p2 + 2 * b0x_p2))) + ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y))))) + (b2x_p2 * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y)))))) + ((a1x * (a1y * ((b2x * (b2y * (b0x - a0x))) + (b2x_p2 * (b0y - a0y))))) + ((a1x_p2 * (b2x * (b2y * (-b0y + a0y)))) + (a1y_p2 * (b2x_p2 * (-b0x + a0x)))))))) + ((b1x * ((b3y_p2 * ((a2x * ((a0x * (3 * a0y + -3 * b0y)) + (b0x * (-3 * a0y + 3 * b0y)))) + ((a1x_p2 * (-3 * a0y + 3 * b0y)) + ((a0x * (2 * a1x * a1y + 2 * a2y * b0x)) + (-2 * a1x * a1y * b0x + (a2y * (-b0x_p2 - a0x_p2))))))) + (b3x_p2 * ((a2y * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (a1y_p2 * (b0y - a0y)))))) + (b1y * ((b3x_p2 * ((a2y * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y)))) + ((a1y_p2 * (3 * a0x + -3 * b0x)) + ((a0y * (-2 * a1x * a1y + -2 * a2x * b0y)) + (2 * a1x * a1y * b0y + (a2x * (b0y_p2 + a0y_p2))))))) + (b3y_p2 * ((a2x * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (a1x_p2 * (-b0x + a0x))))))))),
            ((a1x * ((b3x * ((b3y * ((a0y * (6 * b0y * b2x + ((b2y * (-a0x + b0x)) + -4 * b1x * b1y))) + ((b0y * ((b2y * (a0x + -b0x)) + 4 * b1x * b1y)) + ((b2x * (-3 * a0y_p2 + -3 * b0y_p2)) + (b1y_p2 * (2 * a0x + -2 * b0x)))))) + ((b2y_p2 * ((b1x * (2 * a0y + -2 * b0y)) + (b1y * (b0x - a0x)))) + (b1y * (b2x * (b2y * (b0y - a0y))))))) + ((b3y_p2 * ((b2x * ((a0x * (3 * a0y + -3 * b0y)) + (b0x * (-3 * a0y + 3 * b0y)))) + ((b1x_p2 * (3 * a0y + -3 * b0y)) + ((a0x * (2 * b0x * b2y + -2 * b1x * b1y)) + (2 * b0x * b1x * b1y + (b2y * (-b0x_p2 - a0x_p2))))))) + ((b2y * ((b3x_p2 * (-4 * a0y * b0y + (2 * a0y_p2 + 2 * b0y_p2))) + (b2x * (b3y * ((b1x * (-2 * a0y + 2 * b0y)) + (b1y * (-b0x + a0x))))))) + ((a0y * (b1y_p2 * b3x_p2 + b2x_p2 * b1y * b3y)) + (b0y * (-b1y_p2 * b3x_p2 - b2x_p2 * b1y * b3y))))))) + (a1y * ((b3x * ((b3y * ((a0x * (-6 * b0x * b2y + ((b2x * (a0y + -b0y)) + 4 * b1x * b1y))) + ((b0x * ((b2x * (-a0y + b0y)) + -4 * b1x * b1y)) + ((b2y * (3 * a0x_p2 + 3 * b0x_p2)) + (b1x_p2 * (-2 * a0y + 2 * b0y)))))) + ((b2x * (b2y * ((b1y * (2 * a0x + -2 * b0x)) + (b1x * (b0y - a0y))))) + (b1x * (b2y_p2 * (b0x - a0x)))))) + ((b3x_p2 * ((b2y * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y)))) + ((b1y_p2 * (-3 * a0x + 3 * b0x)) + ((a0y * (-2 * b0y * b2x + 2 * b1x * b1y)) + (-2 * b0y * b1x * b1y + (b2x * (b0y_p2 + a0y_p2))))))) + ((b2x * ((b3y_p2 * (4 * a0x * b0x + (-2 * a0x_p2 + -2 * b0x_p2))) + (b1x * (b2y * (b3y * (-b0x + a0x)))))) + ((b2x_p2 * (b3y * ((b1y * (-2 * a0x + 2 * b0x)) + (b1x * (-b0y + a0y))))) + (b1x_p2 * (b3y_p2 * (b0x - a0x))))))))),
            ((b3x * ((b3y * ((b1x * ((a0y * (-6 * b0y * b2x + (b2y * (a0x + -b0x)))) + ((b0y * (b2y * (-a0x + b0x))) + ((b2x * (3 * a0y_p2 + 3 * b0y_p2)) + (b1y_p2 * (-2 * a0x + 2 * b0x)))))) + (b1y * ((a0x * (6 * b0x * b2y + (b2x * (-a0y + b0y)))) + ((b0x * (b2x * (a0y + -b0y))) + ((b2y * (-3 * a0x_p2 + -3 * b0x_p2)) + (b1x_p2 * (2 * a0y + -2 * b0y)))))))) + ((b3y_p2 * ((a0x * (b0x * (6 * a0y + -6 * b0y))) + ((a0x_p2 * (-3 * a0y + 3 * b0y)) + (b0x_p2 * (-3 * a0y + 3 * b0y))))) + ((b2x * ((b2y_p2 * ((a0x * (-2 * a0y + 2 * b0y)) + (b0x * (2 * a0y + -2 * b0y)))) + (b2y * ((b1x * (b1y * (-b0y + a0y))) + (b1y_p2 * (b0x - a0x)))))) + ((b2x_p2 * (b2y * (-2 * a0y * b0y + (b0y_p2 + a0y_p2)))) + ((b2y_p2 * ((b1x * (b1y * (-b0x + a0x))) + (b1x_p2 * (b0y - a0y)))) + (b2y_p3 * (-2 * a0x * b0x + (b0x_p2 + a0x_p2))))))))) + ((b3x_p2 * ((a0y * ((b0y * ((b3y * (-6 * a0x + 6 * b0x)) + (4 * b1x * b2y + 2 * b1y * b2x))) + ((b1y * (b2y * (3 * a0x + -3 * b0x))) + -b1y_p2 * b1x))) + ((b3y * ((a0x * (3 * a0y_p2 + 3 * b0y_p2)) + (b0x * (-3 * a0y_p2 + -3 * b0y_p2)))) + ((b2y * ((b0y * (b1y * (-3 * a0x + 3 * b0x))) + (b1x * (-2 * a0y_p2 + -2 * b0y_p2)))) + ((b1y * (b2x * (-b0y_p2 - a0y_p2))) + ((b1y_p3 * (-b0x + a0x)) + b1y_p2 * b0y * b1x)))))) + ((b2x * ((b3y_p2 * ((b1x * ((a0x * (-3 * a0y + 3 * b0y)) + (b0x * (3 * a0y + -3 * b0y)))) + (b1y * (-4 * a0x * b0x + (2 * a0x_p2 + 2 * b0x_p2))))) + (b3y * ((b2y * ((b1x * (b1y * (b0x - a0x))) + (b1x_p2 * (-b0y + a0y)))) + (b2y_p2 * (2 * a0x * b0x + (-b0x_p2 - a0x_p2))))))) + ((b3y * ((b2x_p2 * ((b2y * ((a0x * (2 * a0y + -2 * b0y)) + (b0x * (-2 * a0y + 2 * b0y)))) + ((b1x * (b1y * (b0y - a0y))) + (b1y_p2 * (-b0x + a0x))))) + (b2x_p3 * (2 * a0y * b0y + (-b0y_p2 - a0y_p2))))) + ((b3x_p3 * (-3 * b0y_p2 * a0y + (3 * a0y_p2 * b0y + (b0y_p3 - a0y_p3)))) + ((b3y_p2 * ((b1x * (b2y * (-2 * a0x * b0x + (b0x_p2 + a0x_p2)))) + ((b1x_p2 * (b1y * (-b0x + a0x))) + (b1x_p3 * (b0y - a0y))))) + (b3y_p3 * (3 * b0x_p2 * a0x + (-3 * a0x_p2 * b0x + (-b0x_p3 + a0x_p3))))))))))
        ];
    };
}
const D3 = new Degree3();
